/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Queues, across every object -- not just calls.
 *
 * Lifted out of TelephonySettings because a queue is no longer a telephony
 * concept: it can hold cases and leads for a tenant that owns no phone number.
 * TelephonyRoutingPage keeps the voice-only fields (ring timeout, overflow,
 * hold music) and links here for the parts that are not about phones.
 *
 * Two settings decide whether a queue does anything at all, so both are on the
 * card rather than behind an edit dialog:
 *
 *   object scope -- a queue with none accepts no work and looks fine;
 *   members      -- a queue with none never hands anything out and looks fine.
 *
 * Both failures are silent, which is why they are the two things this page
 * warns about in place rather than leaving to be discovered.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import { Add, ArrowBack, Close, Settings, WarningAmber } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const CARD_BORDER = "1px solid #E6E8F0";

/**
 * The objects a queue can hold. Kept short on purpose: this is the set the
 * routing engine and the inbox both understand today. Conversations cover every
 * channel, so WhatsApp and web chat arrive here without a new entry.
 */
const QUEUEABLE = [
  { api_name: "cases", label: "Cases" },
  { api_name: "leads", label: "Leads" },
  { api_name: "conversations", label: "Calls & messages" },
];

const CHANNELS = [
  { value: "any", label: "Any channel" },
  { value: "voice", label: "Voice" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "webchat", label: "Web chat" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

/**
 * The four models the UI offers. `least_active` and `most_available` are new
 * labels over the orderings the voice router has always used; the older
 * spellings stay valid so existing queues keep working untouched.
 */
const STRATEGIES = [
  { value: "least_active", label: "Least loaded", help: "Whoever has the most spare capacity." },
  { value: "round_robin", label: "Round robin", help: "Take turns, skipping anyone who is full." },
  { value: "most_available", label: "Longest idle", help: "Whoever has been free the longest." },
  { value: "fewest_active", label: "Fewest active (legacy)", help: "Same as least loaded." },
  { value: "longest_idle", label: "Longest idle (legacy)", help: "Same as longest idle." },
  {
    value: "skills_then_longest_idle",
    label: "Skills, then longest idle",
    help: "Filter on required skills first.",
  },
];

const MODES = [
  {
    value: "pull",
    label: "Agents take work themselves",
    help: "Items wait in the queue until somebody accepts one.",
  },
  {
    value: "push",
    label: "Assign automatically",
    help: "The engine hands each item to an available agent.",
  },
  {
    value: "hybrid",
    label: "Assign, but stay pullable",
    help: "Assigned automatically; anyone can still take an unaccepted item.",
  },
];

/**
 * One row per (object, channel). A queue routinely wants the same object on
 * more than one channel at different costs -- a voice case is most of an
 * agent's attention, the same case over email is a fraction of it -- and the
 * storage has always been keyed that way. This dialog used to collapse it to
 * one row per object, which meant a second channel could not be added and, if
 * one already existed, was silently dropped on the next save.
 */
function ObjectScopeDialog({ open, onClose, onSave, current, saving, error }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    setRows(
      QUEUEABLE.flatMap((o) =>
        current
          .filter((c) => c.object_api_name === o.api_name)
          .map((c, i) => ({
            key: `${o.api_name}-${c.channel}-${i}`,
            api_name: o.api_name,
            channel: c.channel || "any",
            weight_override: c.weight_override ?? "",
            effective_weight: c.effective_weight ?? null,
            is_default: Boolean(c.is_default),
            is_active: c.is_active !== false,
          }))
      )
    );
  }, [open, current]);

  const rowsFor = (api_name) => rows.filter((r) => r.api_name === api_name);

  const update = (key, patch) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = (api_name) =>
    setRows((rs) => {
      const taken = new Set(rs.filter((r) => r.api_name === api_name).map((r) => r.channel));
      const free = CHANNELS.find((c) => !taken.has(c.value));
      return [
        ...rs,
        {
          key: `${api_name}-new-${Date.now()}`,
          api_name,
          channel: free ? free.value : "any",
          weight_override: "",
          effective_weight: null,
          is_default: false,
          is_active: true,
        },
      ];
    });

  const removeRow = (key) => setRows((rs) => rs.filter((r) => r.key !== key));

  const toggleObject = (api_name, on) =>
    (on ? addRow(api_name) : setRows((rs) => rs.filter((r) => r.api_name !== api_name)));

  // Mirrors QueueObjectReplace.no_duplicate_scope, so the admin sees the
  // problem next to the field rather than as a 422 after pressing Save.
  const duplicates = useMemo(() => {
    const seen = new Set();
    const dupes = new Set();
    rows.forEach((r) => {
      const key = `${r.api_name}|${r.channel}`;
      if (seen.has(key)) dupes.add(key);
      seen.add(key);
    });
    return dupes;
  }, [rows]);

  const tooManyDefaults = rows.filter((r) => r.is_default).length > 1;
  const blocked = duplicates.size > 0 || tooManyDefaults;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>What this queue handles</DialogTitle>
      <DialogContent>
        <MDTypography variant="body2" color="text" mb={2}>
          A queue only accepts the kinds of record listed here. Add a channel for each way the
          record can arrive, and set what one of them costs an agent. Leave the cost blank to use
          the organisation-wide value from Capacity settings.
        </MDTypography>
        <MDTypography variant="caption" color="text" component="div" mb={2}>
          Cost is taken when the item is queued, so changing it here does not re-price work that
          is already waiting.
        </MDTypography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {QUEUEABLE.map((o) => {
          const objectRows = rowsFor(o.api_name);
          return (
            <MDBox key={o.api_name} mb={1.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={objectRows.length > 0}
                    onChange={(e) => toggleObject(o.api_name, e.target.checked)}
                  />
                }
                label={o.label}
              />
              {objectRows.map((row) => {
                const isDupe = duplicates.has(`${row.api_name}|${row.channel}`);
                return (
                  <MDBox key={row.key} display="flex" gap={1} pl={4} mb={1} alignItems="flex-start">
                    <TextField
                      select
                      size="small"
                      label="Channel"
                      value={row.channel}
                      onChange={(e) => update(row.key, { channel: e.target.value })}
                      sx={{ minWidth: 160 }}
                      error={isDupe}
                      helperText={isDupe ? "Already listed for this record" : " "}
                    >
                      {CHANNELS.map((c) => (
                        <MenuItem key={c.value} value={c.value}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      type="number"
                      label="Cost to an agent"
                      value={row.weight_override}
                      onChange={(e) => update(row.key, { weight_override: e.target.value })}
                      sx={{ width: 160 }}
                      helperText={
                        row.weight_override === "" && row.effective_weight
                          ? `Using ${row.effective_weight}`
                          : " "
                      }
                    />
                    <Tooltip title="Remove this channel">
                      <IconButton
                        size="small"
                        onClick={() => removeRow(row.key)}
                        sx={{ mt: 0.5 }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                );
              })}
              {objectRows.length > 0 ? (
                <MDBox pl={4}>
                  <MDButton
                    variant="text"
                    color="info"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => addRow(o.api_name)}
                  >
                    Add channel
                  </MDButton>
                </MDBox>
              ) : null}
            </MDBox>
          );
        })}

        {tooManyDefaults ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            Only one entry may be the queue&apos;s default.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="dark" onClick={onClose}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={saving || blocked}
          onClick={() =>
            onSave({
              objects: rows.map((r) => ({
                object_api_name: r.api_name,
                channel: r.channel,
                weight_override:
                  r.weight_override === "" ? null : Number(r.weight_override),
                is_default: r.is_default,
                is_active: r.is_active,
              })),
            })
          }
        >
          Save
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function NewQueueDialog({ open, onClose, onCreate, saving }) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState("pull");

  useEffect(() => {
    if (open) {
      setName("");
      setMode("pull");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>New queue</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="normal"
          autoFocus
        />
        <TextField
          select
          fullWidth
          size="small"
          label="How work is handed out"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          margin="normal"
          helperText={MODES.find((m) => m.value === mode)?.help}
        >
          {MODES.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </TextField>
        <Alert severity="info" sx={{ mt: 1 }}>
          You will need to add members and choose what it handles before it can do anything.
        </Alert>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="dark" onClick={onClose}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={!name.trim() || saving}
          // `channel` on the queue row is vestigial now that scope is held per
          // (object, channel); send "any" rather than letting the column
          // default to "voice" and label a cases queue a phone queue.
          onClick={() => onCreate({ name: name.trim(), routing_mode: mode, channel: "any" })}
        >
          Create
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function QueueCard({ queue, axiosInstance, onChanged, setError, setToast, skills }) {
  const [objects, setObjects] = useState([]);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Kept beside the dialog rather than on the page: a 409 here names the
  // objects whose open work blocks the change, and the page-level banner sits
  // behind the modal where nobody reads it.
  const [scopeError, setScopeError] = useState("");

  const loadObjects = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/call-center/queues/${queue.id}/objects`);
      setObjects(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not load this queue's object scope."));
    }
  }, [axiosInstance, queue.id, setError]);

  useEffect(() => {
    loadObjects();
  }, [loadObjects]);

  const saveField = async (patch, message) => {
    setError("");
    try {
      await axiosInstance.put(`/call-center/queues/${queue.id}`, patch);
      setToast(message);
      onChanged();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save that."));
      onChanged();
    }
  };

  const saveScope = async (payload) => {
    setSaving(true);
    setError("");
    setScopeError("");
    try {
      await axiosInstance.put(`/call-center/queues/${queue.id}/objects`, payload);
      setScopeOpen(false);
      await loadObjects();
      onChanged();
      setToast("Object scope saved.");
    } catch (err) {
      // 409 here means open work would be stranded, and the server's wording
      // names which objects. Generic text would lose that.
      setScopeError(getApiErrorMessage(err, "Could not save the object scope."));
    } finally {
      setSaving(false);
    }
  };

  const unstaffed = !queue.member_count;
  const unscoped = objects.length === 0;

  return (
    <Card sx={{ p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px", mb: 3 }}>
      <MDBox display="flex" alignItems="flex-start" gap={1} mb={1}>
        <MDBox flexGrow={1}>
          <MDTypography variant="h6" fontWeight="medium">
            {queue.name}
            {!queue.is_active ? (
              <Chip size="small" variant="outlined" label="Inactive" sx={{ ml: 1 }} />
            ) : null}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {queue.queue_key} · {queue.member_count} member
            {queue.member_count === 1 ? "" : "s"} · {queue.pending_items} waiting
          </MDTypography>
        </MDBox>
        <Tooltip title="Voice-specific settings: ring timeout, overflow, hold music">
          <span>
            <MDButton
              variant="text"
              color="dark"
              size="small"
              startIcon={<Settings />}
              href="/settings/telephony/routing"
            >
              Voice settings
            </MDButton>
          </span>
        </Tooltip>
      </MDBox>

      {unscoped ? (
        <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
          This queue handles nothing yet, so no record can be put in it. Choose what it handles
          below.
        </Alert>
      ) : null}
      {unstaffed ? (
        <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
          Nobody is a member, so work that lands here is never offered to anyone and nobody can
          see it. Add members in Queues &amp; routing.
        </Alert>
      ) : null}

      <Divider sx={{ my: 2 }} />

      <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
        <MDTypography variant="button" fontWeight="medium" color="text">
          Handles:
        </MDTypography>
        {objects.length === 0 ? (
          <MDTypography variant="body2" color="text">
            nothing
          </MDTypography>
        ) : (
          objects.map((o) => (
            <Tooltip key={o.id} title={`Worth ${o.effective_weight} of an agent's capacity`}>
              <Chip
                size="small"
                variant="outlined"
                label={`${o.label}${o.channel !== "any" ? ` · ${o.channel}` : ""}`}
              />
            </Tooltip>
          ))
        )}
        <MDButton variant="text" color="info" size="small" onClick={() => setScopeOpen(true)}>
          Change
        </MDButton>
      </MDBox>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="How work is handed out"
            value={queue.routing_mode}
            onChange={(e) =>
              saveField({ routing_mode: e.target.value }, "Assignment mode saved.")
            }
            helperText={MODES.find((m) => m.value === queue.routing_mode)?.help}
          >
            {MODES.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Who gets it"
            value={queue.routing_strategy}
            onChange={(e) =>
              saveField({ routing_strategy: e.target.value }, "Routing model saved.")
            }
            helperText={STRATEGIES.find((s) => s.value === queue.routing_strategy)?.help}
          >
            {STRATEGIES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select
            size="small"
            label="Required skills"
            SelectProps={{
              multiple: true,
              value: queue.required_skills || [],
              onChange: (e) =>
                saveField({ required_skills: e.target.value }, "Required skills saved."),
              renderValue: (codes) =>
                codes.length
                  ? codes
                      .map((c) => skills.find((s) => s.code === c)?.name || c)
                      .join(", ")
                  : "Anyone",
            }}
            helperText={
              (queue.required_skills || []).length
                ? "Reaches anyone holding at least one of these."
                : "Anyone in this queue can take the work."
            }
          >
            {skills.length === 0 ? (
              <MenuItem disabled value="">
                No skills defined yet
              </MenuItem>
            ) : (
              skills.map((s) => (
                <MenuItem key={s.id} value={s.code}>
                  {s.name}
                </MenuItem>
              ))
            )}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Max items per agent"
            defaultValue={queue.max_items_per_agent ?? ""}
            onBlur={(e) =>
              saveField(
                { max_items_per_agent: e.target.value === "" ? null : Number(e.target.value) },
                "Per-agent limit saved."
              )
            }
            helperText="Blank means only the agent's overall capacity applies."
          />
        </Grid>
      </Grid>

      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Checkbox
            checked={Boolean(queue.owns_records)}
            onChange={(e) => saveField({ owns_records: e.target.checked }, "Saved.")}
          />
        }
        label="Records in this queue show the queue as their owner"
      />

      <ObjectScopeDialog
        open={scopeOpen}
        onClose={() => {
          setScopeError("");
          setScopeOpen(false);
        }}
        onSave={saveScope}
        current={objects}
        saving={saving}
        error={scopeError}
      />
    </Card>
  );
}

function QueuesSettingsPage() {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === "v2";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [queues, setQueues] = useState([]);
  // The catalogue backing the "Required skills" picker. Loaded once here
  // rather than per card, which would be one request per queue on a page that
  // routinely shows a dozen.
  const [skills, setSkills] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [res, skillsRes] = await Promise.all([
        axiosInstance.get("/call-center/queues", { params: { include_inactive: true } }),
        // Only skills a queue may be set to require; deactivated ones stay on
        // the agents who hold them but must not be selectable here.
        axiosInstance
          .get("/call-center/skills", { params: { include_inactive: false } })
          .catch(() => ({ data: [] })),
      ]);
      setQueues(Array.isArray(res.data) ? res.data : []);
      setSkills(Array.isArray(skillsRes.data) ? skillsRes.data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to load queues."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (payload) => {
    setSaving(true);
    setError("");
    try {
      await axiosInstance.post("/call-center/queues", payload);
      setNewOpen(false);
      setToast("Queue created.");
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not create that queue."));
    } finally {
      setSaving(false);
    }
  };

  const problems = useMemo(
    () => queues.filter((q) => q.is_active && !q.member_count).length,
    [queues]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {isV2 ? (
          <PageHeader
            title="Queues"
            summary="A queue holds work of whatever kind you tell it to — cases, leads, calls and messages — and hands it to the people in it."
            actions={(
              <>
                <MDButton variant="outlined" color="dark" onClick={() => navigate("/settings/queues/capacity")}>
                  Capacity &amp; values
                </MDButton>
                <MDButton variant="gradient" color="info" startIcon={<Add />} onClick={() => setNewOpen(true)}>
                  New queue
                </MDButton>
              </>
            )}
          />
        ) : (
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate("/settings")}>
            <ArrowBack />
          </IconButton>
          <MDBox flexGrow={1}>
            <MDTypography variant="h5" fontWeight="medium">
              Queues
            </MDTypography>
            <MDTypography variant="body2" color="text">
              A queue holds work of whatever kind you tell it to — cases, leads, calls and
              messages — and hands it to the people in it.
            </MDTypography>
          </MDBox>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={() => navigate("/settings/queues/capacity")}
          >
            Capacity &amp; values
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            startIcon={<Add />}
            onClick={() => setNewOpen(true)}
          >
            New queue
          </MDButton>
        </MDBox>
        )}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}
        {problems ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {problems} active queue{problems === 1 ? " has" : "s have"} no members. Work that lands
            there is invisible to everyone.
          </Alert>
        ) : null}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={6}>
            <CircularProgress size={26} />
          </MDBox>
        ) : queues.length === 0 ? (
          <Card sx={{ p: 4, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px" }}>
            <MDTypography variant="body2" color="text">
              No queues yet.
            </MDTypography>
          </Card>
        ) : (
          queues.map((q) => (
            <QueueCard
              key={q.id}
              queue={q}
              axiosInstance={axiosInstance}
              onChanged={load}
              setError={setError}
              setToast={setToast}
              skills={skills}
            />
          ))
        )}
      </MDBox>

      <NewQueueDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreate={create}
        saving={saving}
      />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </DashboardLayout>
  );
}

export default QueuesSettingsPage;
